import { useLocation, useNavigate } from "react-router-dom";
import { useEffect, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Home, ArrowRight, Search } from "lucide-react";

const NotFound = () => {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    console.error(
      "404 Error: User attempted to access non-existent route:",
      location.pathname
    );
  }, [location.pathname]);

  // Define available routes with metadata
  const availableRoutes = useMemo(() => [
    { path: '/dashboard', label: 'Dashboard', description: 'Main overview and key metrics', keywords: ['home', 'main', 'overview'] },
    { path: '/dashboard/accounting/pl', label: 'Profit & Loss', description: 'P&L statements and reports', keywords: ['profit', 'loss', 'income', 'statement'] },
    { path: '/dashboard/accounting/cash-flow', label: 'Cash Flow', description: 'Cash flow analysis', keywords: ['cash', 'flow', 'money'] },
    { path: '/dashboard/accounting/balance-sheet', label: 'Balance Sheet', description: 'Assets and liabilities', keywords: ['balance', 'assets', 'liabilities'] },
    { path: '/dashboard/accounting/tax-center', label: 'Tax Center', description: 'Tax documents and filings', keywords: ['tax', 'irs', 'filing'] },
    { path: '/dashboard/accounting/reconciliation', label: 'Reconciliation', description: 'Bank reconciliation', keywords: ['reconcile', 'match', 'verify'] },
    { path: '/dashboard/finance/sales', label: 'Sales Insights', description: 'Sales data and analytics', keywords: ['sales', 'revenue', 'transactions', 'menu', 'items', 'performance', 'food', 'platform'] },
    { path: '/dashboard/finance/expenses', label: 'Expenses', description: 'Expense tracking and management', keywords: ['expenses', 'costs', 'spending'] },
    { path: '/dashboard/operations/inventory', label: 'Inventory', description: 'Inventory management', keywords: ['inventory', 'stock', 'pantry'] },
    { path: '/dashboard/operations/menu', label: 'Menu Management', description: 'Menu configuration', keywords: ['menu', 'items', 'dishes'] },
    { path: '/dashboard/vendors', label: 'Vendors', description: 'Vendor management', keywords: ['vendors', 'suppliers', 'partners'] },
    { path: '/dashboard/data-center', label: 'Data Center', description: 'Data uploads and management', keywords: ['data', 'upload', 'import'] },
    { path: '/dashboard/settings', label: 'Settings', description: 'Account and app settings', keywords: ['settings', 'preferences', 'config'] },
  ], []);

  // Calculate similarity score between attempted path and available routes
  const suggestedRoutes = useMemo(() => {
    const attemptedPath = location.pathname.toLowerCase();
    const pathSegments = attemptedPath.split('/').filter(Boolean);
    
    return availableRoutes
      .map(route => {
        let score = 0;
        const routeSegments = route.path.toLowerCase().split('/').filter(Boolean);
        
        // Check for matching path segments
        pathSegments.forEach(segment => {
          if (routeSegments.some(rs => rs.includes(segment) || segment.includes(rs))) {
            score += 3;
          }
          if (route.keywords.some(keyword => keyword.includes(segment) || segment.includes(keyword))) {
            score += 2;
          }
          if (route.label.toLowerCase().includes(segment)) {
            score += 1;
          }
        });
        
        return { ...route, score };
      })
      .filter(route => route.score > 0)
      .sort((a, b) => b.score - a.score)
      .slice(0, 3);
  }, [location.pathname, availableRoutes]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-6">
      <Card className="w-full max-w-2xl">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4 flex h-20 w-20 items-center justify-center rounded-full bg-muted">
            <Search className="h-10 w-10 text-muted-foreground" />
          </div>
          <CardTitle className="text-4xl font-bold">404 - Page Not Found</CardTitle>
          <CardDescription className="text-lg mt-2">
            We couldn't find the page you're looking for: <code className="text-sm bg-muted px-2 py-1 rounded">{location.pathname}</code>
          </CardDescription>
        </CardHeader>
        
        <CardContent className="space-y-6">
          {suggestedRoutes.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-muted-foreground mb-3">
                Were you looking for one of these?
              </h3>
              <div className="space-y-2">
                {suggestedRoutes.map((route) => (
                  <Button
                    key={route.path}
                    variant="outline"
                    className="w-full justify-between h-auto py-3 px-4"
                    onClick={() => navigate(route.path)}
                  >
                    <div className="text-left">
                      <div className="font-semibold">{route.label}</div>
                      <div className="text-sm text-muted-foreground">{route.description}</div>
                    </div>
                    <ArrowRight className="h-4 w-4 shrink-0 ml-2" />
                  </Button>
                ))}
              </div>
            </div>
          )}
          
          <div className="flex gap-3 pt-4">
            <Button 
              onClick={() => navigate('/dashboard')} 
              className="flex-1 gap-2"
            >
              <Home className="h-4 w-4" />
              Go to Dashboard
            </Button>
            <Button 
              onClick={() => navigate(-1)} 
              variant="outline"
              className="flex-1"
            >
              Go Back
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default NotFound;
